import React, { useEffect, useState, useCallback } from 'react';
import PageContainer from './PageContainer';
import axios from 'axios';
import Loader from './components/Loader';
import ConfirmModal from './components/ConfirmModal';

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

export default function ManagerMapping() {
  const [associateSearchResults, setAssociateSearchResults] = useState([]);
  const [managerSearchResults, setManagerSearchResults] = useState([]);
  const [managerFilterResults, setManagerFilterResults] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [managerFilter, setManagerFilter] = useState('');
  const [form, setForm] = useState({ associate: '', manager: '' });
  const [associateSearch, setAssociateSearch] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [managerFilterSearch, setManagerFilterSearch] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAssociateDropdown, setShowAssociateDropdown] = useState(false);
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);
  const [showManagerFilterDropdown, setShowManagerFilterDropdown] = useState(false);
  const [associateFilter, setAssociateFilter] = useState('');

  useEffect(() => {
    axios.get('/api/csrf/', { withCredentials: true }).catch(() => { });
    axios.defaults.withCredentials = true;
    // Only load mappings initially
    loadMappings();

    // Click outside handler to close dropdowns
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowAssociateDropdown(false);
        setShowManagerDropdown(false);
        setShowManagerFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMappings = async () => {
    setLoading(true);
    try {
      const mappingsRes = await axios.get('/api/v1/manager-mappings/');
      setMappings(mappingsRes.data.results || []);
    } catch (err) {
      console.error('Failed to load mappings', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const useDebounce = (callback, delay) => {
    const [debounceTimer, setDebounceTimer] = useState(null);

    return useCallback((...args) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      setDebounceTimer(setTimeout(() => callback(...args), delay));
    }, [callback, delay, debounceTimer]);
  };

  const searchUsers = async (query, roleFilter = null) => {
    if (query.length < 3) return [];
    try {
      let url = `/api/v1/users/?search=${encodeURIComponent(query)}&page_size=20`;
      if (roleFilter) {
        url += `&role_name=${encodeURIComponent(roleFilter)}`;
      }
      const res = await axios.get(url);
      return res.data.results || [];
    } catch (err) {
      console.error('Search failed', err);
      return [];
    }
  };

  const searchManagers = async (query) => {
    return await searchUsers(query, 'Manager');
  };

  const searchAssociates = async (query) => {
    // Search for users who are not managers
    const allResults = await searchUsers(query);
    return allResults.filter(user => {
      const roleName = (user.role_name || user.role?.name || '').toLowerCase();
      return !roleName.includes('manager');
    });
  };

  const debouncedAssociateSearch = useDebounce(async (query) => {
    if (query.length >= 3) {
      const results = await searchAssociates(query);
      setAssociateSearchResults(results);
      setShowAssociateDropdown(true); // Show dropdown even if no results to display "No matches"
    } else {
      setAssociateSearchResults([]);
      setShowAssociateDropdown(false);
    }
  }, 300);

  const debouncedManagerSearch = useDebounce(async (query) => {
    if (query.length >= 3) {
      const results = await searchManagers(query);
      setManagerSearchResults(results);
      setShowManagerDropdown(true); // Show dropdown even if no results to display "No matches"
    } else {
      setManagerSearchResults([]);
      setShowManagerDropdown(false);
    }
  }, 300);

  const debouncedManagerFilterSearch = useDebounce(async (query) => {
    if (query.length >= 3) {
      const results = await searchManagers(query);
      setManagerFilterResults(results);
      setShowManagerFilterDropdown(true); // Show dropdown even if no results to display "No matches"
    } else {
      setManagerFilterResults([]);
      setShowManagerFilterDropdown(false);
    }
  }, 300);

  const handleAssociateSearchChange = (e) => {
    const value = e.target.value;
    setAssociateSearch(value);
    debouncedAssociateSearch(value);
  };

  const handleManagerSearchChange = (e) => {
    const value = e.target.value;
    setManagerSearch(value);
    debouncedManagerSearch(value);
  };

  const handleManagerFilterSearchChange = (e) => {
    const value = e.target.value;
    setManagerFilterSearch(value);
    debouncedManagerFilterSearch(value);
  };

  const selectAssociate = (user) => {
    setForm({ ...form, associate: user.id });
    setAssociateSearch(`${user.full_name} (${user.email})`);
    setShowAssociateDropdown(false);
  };

  const selectManager = (user) => {
    setForm({ ...form, manager: user.id });
    setManagerSearch(`${user.full_name} (${user.email})`);
    setShowManagerDropdown(false);
  };

  const selectManagerFilter = (user) => {
    setManagerFilter(user.id);
    setManagerFilterSearch(`${user.full_name} (${user.email})`);
    setShowManagerFilterDropdown(false);
  };

  const clearAssociateSearch = () => {
    setAssociateSearch('');
    setForm({ ...form, associate: '' });
    setAssociateSearchResults([]);
    setShowAssociateDropdown(false);
  };

  const clearManagerSearch = () => {
    setManagerSearch('');
    setForm({ ...form, manager: '' });
    setManagerSearchResults([]);
    setShowManagerDropdown(false);
  };

  const clearManagerFilter = () => {
    setManagerFilterSearch('');
    setManagerFilter('');
    setManagerFilterResults([]);
    setShowManagerFilterDropdown(false);
  };

  const handleFormChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreateMapping = async e => {
    e.preventDefault();
    setLoading(true);
    setMessages([]); // Clear previous messages
    try {
      await axios.post('/api/v1/manager-mappings/', {
        associate: form.associate,
        manager: form.manager
      }, { headers: { 'X-CSRFToken': getCookie('csrftoken') }, withCredentials: true });
      setMessages([{ tags: 'success', text: 'Mapping created/updated.' }]);
      setForm({ associate: '', manager: '' });
      clearAssociateSearch();
      clearManagerSearch();
      // Refresh mappings
      await loadMappings();
      // Clear success message after 3 seconds
      setTimeout(() => setMessages([]), 3000);
    } catch (err) {
      console.error('Create mapping error:', err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        (err.response?.status === 403 ? 'Permission denied. You may not have the required permissions.' :
          err.response?.status === 400 ? 'Invalid data. Please check your selections.' :
            err.response?.status === 500 ? 'Server error. Please try again later.' :
              'Error creating/updating mapping. Please try again.');
      setMessages([{ tags: 'error', text: errorMessage }]);
    }
    setLoading(false);
  };

  const handleDeleteMapping = async id => {
    setDeleting(true);
    setMessages([]); // Clear previous messages
    try {
      await axios.delete(`/api/v1/manager-mappings/${id}/`, {
        headers: { 'X-CSRFToken': getCookie('csrftoken') },
        withCredentials: true
      });
      setMessages([{ tags: 'success', text: 'Mapping deleted.' }]);
      setMappings(mappings.filter(m => m.id !== id));
      setDeleteTarget(null);
      // Clear success message after 3 seconds
      setTimeout(() => setMessages([]), 3000);
    } catch (err) {
      console.error('Delete mapping error:', err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        (err.response?.status === 403 ? 'Permission denied. You may not have the required permissions.' :
          err.response?.status === 404 ? 'Mapping not found. It may have already been deleted.' :
            err.response?.status === 500 ? 'Server error. Please try again later.' :
              'Error deleting mapping. Please try again.');
      setMessages([{ tags: 'error', text: errorMessage }]);
    }
    setDeleting(false);
  };

  const filteredMappings = mappings.filter(m => {
    // Filter by Manager
    if (managerFilter) {
      const mId = m.manager && typeof m.manager === 'object' ? String(m.manager.id) : String(m.manager);
      if (mId !== String(managerFilter)) return false;
    }
    // Filter by Associate (Name or Email)
    if (associateFilter.trim()) {
      const associateName = (m.associate_name || m.associate?.full_name || '').toLowerCase();
      const associateEmail = (m.associate_email || m.associate?.email || '').toLowerCase();
      const query = associateFilter.toLowerCase().trim();
      if (!associateName.includes(query) && !associateEmail.includes(query)) return false;
    }
    return true;
  });

  return (
    <PageContainer>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Career Associate–Manager Mappings</h1>
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Create or Update Mapping</h2>
        <form className="grid grid-cols-1 md:grid-cols-3 gap-4" onSubmit={handleCreateMapping}>
          {/* Associate Search Field */}
          <div className="relative col-span-1">
            <input
              type="text"
              placeholder="Search for Career Associate (type 3+ characters)"
              value={associateSearch}
              onChange={handleAssociateSearchChange}
              onFocus={() => (associateSearchResults.length > 0 || associateSearch.length >= 3) && setShowAssociateDropdown(true)}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
            />
            {form.associate && (
              <button
                type="button"
                onClick={clearAssociateSearch}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
            {showAssociateDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {associateSearchResults.length > 0 ? (
                  associateSearchResults.map(user => (
                    <div
                      key={user.id}
                      onClick={() => selectAssociate(user)}
                      className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100"
                    >
                      <div className="font-medium text-sm">{user.full_name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                  ))
                ) : (
                  associateSearch.length >= 3 && (
                    <div className="px-4 py-2 text-gray-500 text-sm">
                       No users match your search
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          {/* Manager Search Field */}
          <div className="relative col-span-1">
            <input
              type="text"
              placeholder="Search for Manager (type 3+ characters)"
              value={managerSearch}
              onChange={handleManagerSearchChange}
              onFocus={() => (managerSearchResults.length > 0 || managerSearch.length >= 3) && setShowManagerDropdown(true)}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
            />
            {form.manager && (
              <button
                type="button"
                onClick={clearManagerSearch}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
            {showManagerDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {managerSearchResults.length > 0 ? (
                  managerSearchResults.map(user => (
                    <div
                      key={user.id}
                      onClick={() => selectManager(user)}
                      className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100"
                    >
                      <div className="font-medium text-sm">{user.full_name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                  ))
                ) : (
                  managerSearch.length >= 3 && (
                    <div className="px-4 py-2 text-gray-500 text-sm">
                      No managers match your search
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md shadow col-span-1 md:col-span-1 disabled:opacity-50"
            disabled={loading || !form.associate || !form.manager}
          >
            Add/Update Mapping
          </button>
        </form>
      </div>
      {messages.length > 0 && (
        <div className="mb-6">
          {messages.map((m, i) => (
            <div key={i} className={`p-4 rounded-md ${m.tags === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{m.text}</div>
          ))}
        </div>
      )}

      {/* Filters (Manager & Career Associate) */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        {/* Manager Filter */}
        <div className="relative w-full md:w-80">
          <input
            type="text"
            placeholder="Filter by Manager (type 3+ characters)"
            value={managerFilterSearch}
            onChange={handleManagerFilterSearchChange}
            onFocus={() => (managerFilterResults.length > 0 || managerFilterSearch.length >= 3) && setShowManagerFilterDropdown(true)}
            className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
          />
          {managerFilter && (
            <button
              type="button"
              onClick={clearManagerFilter}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
          {showManagerFilterDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {managerFilterResults.length > 0 ? (
                managerFilterResults.map(user => (
                  <div
                    key={user.id}
                    onClick={() => selectManagerFilter(user)}
                    className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100"
                  >
                    <div className="font-medium text-sm">{user.full_name}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </div>
                ))
              ) : (
                managerFilterSearch.length >= 3 && (
                  <div className="px-4 py-2 text-gray-500 text-sm">
                    No managers match your search
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Associate Filter */}
        <div className="relative w-full md:w-80">
          <input
            type="text"
            placeholder="Filter by Career Associate Name/Email"
            value={associateFilter}
            onChange={(e) => setAssociateFilter(e.target.value)}
            className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500"
          />
          {associateFilter && (
            <button
              type="button"
              onClick={() => setAssociateFilter('')}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <div className="grid gap-4">
        {loading ? (
          <Loader />
        ) : (filteredMappings.length > 0 ? filteredMappings.map(mapping => {
          // Support both legacy nested object response and flat serializer fields
          const associateName = mapping.associate_name || mapping.associate?.full_name || '';
          const associateEmail = mapping.associate_email || mapping.associate?.email || '';
          const managerName = mapping.manager_name || mapping.manager?.full_name || '';
          const managerEmail = mapping.manager_email || mapping.manager?.email || '';
          const createdAt = mapping.created_at ? new Date(mapping.created_at).toLocaleString() : '';
          return (
            <div key={mapping.id} className="bg-white shadow rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-indigo-600 font-semibold text-sm">{associateName}</p>
                <p className="text-gray-600 text-sm">{associateEmail}</p>
                <p className="text-gray-400 text-xs mt-1">→ Managed by <strong>{managerName}</strong> ({managerEmail})</p>
                <p className="text-gray-400 text-xs mt-1">Created: {createdAt}</p>
              </div>
              <button
                type="button"
                className="text-red-500 hover:underline text-sm mt-4 md:mt-0"
                onClick={() => setDeleteTarget(mapping)}
              >
                Delete
              </button>
            </div>
          );
        }) : <p className="text-gray-500 text-sm">No mappings found.</p>)}
      </div>
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Manager Mapping"
        message={`Are you sure you want to delete the mapping for ${
          deleteTarget ? deleteTarget.associate_name || deleteTarget.associate?.full_name || 'this Career Associate' : ''
        }?`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={() => handleDeleteMapping(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
        danger={true}
      />
    </PageContainer>
  );
}
